import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    company_name: '',
    address: '',
    message: ''
  });
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const formDataToSend = new FormData();
      
      // Add form fields
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('contact', formData.contact);
      formDataToSend.append('company_name', formData.company_name);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('message', formData.message);
      
      // Add files
      files.forEach(file => {
        formDataToSend.append('files', file);
      });

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/send/email`, {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (result.status === 'success') {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          contact: '',
          company_name: '',
          address: '',
          message: ''
        });
        setFiles([]);
        // Reset file input
        const fileInput = document.getElementById('document-upload');
        if (fileInput) fileInput.value = '';
      } else {
        setError(result.message || 'Failed to send email');
      }
    } catch (error) {
      setError('Failed to send email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Contact Hospital Build Pvt Ltd | MGPS Supplier India</title>
        <meta name="description" content="Contact Hospital Build Pvt Ltd for inquiries about Medical Gas Pipeline Systems (MGPS), hospital equipment, and turnkey hospital projects in India." />
        <meta name="keywords" content="Contact Hospital Build, MGPS Supplier Contact, Hospital Equipment Inquiry, Medical Gas Pipeline System India" />
        <link rel="canonical" href="https://www.hospitalbuild.in/contact" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Left Banner */}
          <div className="relative h-[400px] overflow-hidden rounded-lg bg-[#2C2C90]">
            <div className="absolute inset-0">
              <img 
                src="/images/contact-banner.svg" 
                alt="Contact Us" 
                className="w-[100%] h-[100%] object-cover"
              />
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-[#2C2C90]/180">
              <h1 className="text-5xl font-bold text-white">Contact Us</h1>
            </div>
          </div>

          {/* Working Hours Card */}
          <div className="bg-[#2C2C90] text-white p-6 rounded-lg h-auto">
            <h3 className="text-2xl font-medium mb-4">Working Hours</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <span className="w-28">Monday</span>
                <span className="flex-grow border-b border-dotted border-white mx-2"></span>
                <span>9:30 AM – 7:00 PM</span>
              </div>
              <div className="flex items-center">
                <span className="w-28">Tuesday</span>
                <span className="flex-grow border-b border-dotted border-white mx-2"></span>
                <span>9:30 AM – 7:00 PM</span>
              </div>
              <div className="flex items-center">
                <span className="w-28">Wednesday</span>
                <span className="flex-grow border-b border-dotted border-white mx-2"></span>
                <span>9:30 AM – 7:00 PM</span>
              </div>
              <div className="flex items-center">
                <span className="w-28">Thursday</span>
                <span className="flex-grow border-b border-dotted border-white mx-2"></span>
                <span>9:30 AM – 7:00 PM</span>
              </div>
              <div className="flex items-center">
                <span className="w-28">Friday</span>
                <span className="flex-grow border-b border-dotted border-white mx-2"></span>
                <span>9:30 AM – 7:00 PM</span>
              </div>
              <div className="flex items-center">
                <span className="w-28">Saturday</span>
                <span className="flex-grow border-b border-dotted border-white mx-2"></span>
                <span>9:30 AM – 2:00 PM</span>
              </div>
              <div className="flex items-center">
                <span className="w-28">Sunday</span>
                <span className="flex-grow border-b border-dotted border-white mx-2"></span>
                <span>Closed</span>
              </div>
              <div className="flex items-center">
                <span className="w-28">Holidays</span>
                <span className="flex-grow border-b border-dotted border-white mx-2"></span>
                <span>Closed</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div>
            <div className="bg-gray-100 rounded-lg p-6 md:sticky md:top-6">
              {/* <div className="text-[#2C2C90] mb-2">GET IN TOUCH —</div> */}
              <h2 className="text-2xl text-[#2C2C90] mb-6">Contact Us</h2>

              <div className="space-y-4">
                {/* Call Us Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                  <h3 className="text-lg font-medium text-[#2C2C90] mb-2">Call Us</h3>
                  <div className="flex items-center gap-3 text-gray-700">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50">
                      <Phone size={18} className="text-[#2C2C90]" />
                    </span>
                    <p>+91-8726620213</p>
                  </div>
                </div>

                {/* Email Us Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                  <h3 className="text-lg font-medium text-[#2C2C90] mb-2">Email Us</h3>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50">
                      <Mail size={18} className="text-[#2C2C90]" />
                    </span>
                    <p className="text-[#2C2C90]">hospitalbuild24@gmail.com</p>
                  </div>
                </div>

                {/* Address Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                  <h3 className="text-lg font-medium text-[#2C2C90] mb-2">Our Address</h3>
                  <div className="flex items-start gap-3 text-gray-700">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 mt-0.5">
                      <MapPin size={18} className="text-[#2C2C90]" />
                    </span>
                    {/* <p>303, A09 GDITL Tower, Netaji Shubhas Place Pitampura, New Delhi-110034</p> */}
                    <p>New Delhi-110034</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-100 p-6 rounded-lg">
            {/* <div className="text-[#2C2C90] mb-1">GET IN TOUCH —</div> */}
            <h4 className="text-xl mb-6">Support Is Online</h4>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Success Message */}
              {success && (
                <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Email sent successfully! We'll get back to you soon.
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {error}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="p-3 border rounded bg-white"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Id"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="p-3 border rounded bg-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="tel"
                  name="contact"
                  placeholder="Mobile Number"
                  value={formData.contact}
                  onChange={handleInputChange}
                  required
                  className="p-3 border rounded bg-white"
                />
                <input
                  type="text"
                  name="company_name"
                  placeholder="Company Name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  required
                  className="p-3 border rounded bg-white"
                />
              </div>
              <input
                type="text"
                name="address"
                placeholder="Address"
                value={formData.address}
                onChange={handleInputChange}
                required
                className="w-full p-3 border rounded bg-white"
              />
              <textarea
                name="message"
                placeholder="Message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows="3"
                className="w-full p-3 border rounded bg-white resize-none"
              ></textarea>
              
              {/* Document Attachment Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attach Documents (Optional)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    className="hidden"
                    id="document-upload"
                    onChange={handleFileChange}
                  />
                  
                  {files.length > 0 ? (
                    <div>
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                      </div>
                      <p className="text-green-600 font-medium mb-2">Files Selected ({files.length})</p>
                      <div className="space-y-1 mb-3">
                        {files.map((file, index) => (
                          <div key={index} className="text-sm text-gray-700 bg-gray-50 px-2 py-1 rounded">
                            ✓ {file.name}
                          </div>
                        ))}
                      </div>
                      <label htmlFor="document-upload" className="cursor-pointer text-blue-600 hover:text-blue-800 text-sm">
                        Click to add more files
                      </label>
                    </div>
                  ) : (
                    <label htmlFor="document-upload" className="cursor-pointer">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                      </div>
                      <p className="text-blue-600 font-medium mb-1">Browse Files</p>
                      <p className="text-gray-600 text-sm">Drag and drop files here or click to browse</p>
                      <p className="text-gray-500 text-xs mt-1">Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB each)</p>
                    </label>
                  )}
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#2C2C90] text-white py-3 rounded text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </div>
                ) : (
                  'Submit'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact; 